import { FormikContextType } from 'formik';
import { Moment, isMoment } from 'moment';
import { useTranslation } from 'react-i18next';
import { RoutePath, TourRoutePath } from './routes';

export const addBusinessDays = (originalDate: Moment, daysToAdd: number) => {
  const Sunday = 0;
  const Saturday = 6;
  const newDate = originalDate.clone();

  while (daysToAdd > 0) {
    newDate.add(1, 'days');
    if (![Saturday, Sunday].includes(newDate.day())) {
      daysToAdd--;
    }
  }

  return newDate;
};

export const trim = (value: any) => {
  if (typeof value === 'string') return value.trim();
  if (isMoment(value)) return value;
  if (value instanceof Object) {
    return Object.entries(value).reduce((acc, [key, val]) => ({
      ...acc,
      [key]: trim(val)
    }), {});
  }
  return value;
};

export const objectsDiffer = (obj1: object, obj2: object) => {
  if (!obj1 && !obj2) return false;
  if (!obj1 || !obj2) return true;
  if (isMoment(obj1)) return !obj1.isSame(obj2);
  return Object.entries(obj1).some(([key, val]) => {
    if (val instanceof Object) return objectsDiffer(val, obj2[key]);
    return (val || obj2[key]) && val != obj2[key];
  });
};

export const scrollTo = (el: string | HTMLElement, focus: boolean = true) => {
  if (typeof el === 'string') el = document.querySelector(el) as HTMLElement;
  if (!el) return;
  if (focus) el.focus({ preventScroll: true });
  if ('scrollBehavior' in document.documentElement.style) {
    window.scrollTo({ top: el.offsetTop, behavior: 'smooth' });
  }
  else window.scrollTo(0, el.offsetTop);
};

export const popupLink = (href, name) =>
  `javascript:window.open('${href}', '${name}', 'resizable=yes,scrollbars=yes,width=600,height=500,left=50,top=50')`;

let viewportContent: string;
let responsiveEnabled = false;
let locked = false;
export const toggleResponsive = (enable = !responsiveEnabled, andLock?: boolean) => {
  if (locked || enable === responsiveEnabled) return;
  locked = andLock;
  const viewportMeta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;
  if (enable) {
    viewportContent = viewportMeta.content;
    viewportMeta.content = viewportMeta.content.replace(/width=[^,]*/, 'width=device-width');
    document.getElementById('N600wrapper').classList.add('responsive');
  }
  else {
    viewportMeta.content = viewportContent;
    document.getElementById('N600wrapper').classList.remove('responsive');
  }
  responsiveEnabled = enable;
};

export const dataLayerPush = (data = {}) => {
  if (process.env.NODE_ENV === 'production') {
    window.dataLayer.push({
      'event': 'dig-custom-event',
      ...data
    });
  }
};

export const NumberDisplayFormat = ({num}) => {
  const { t } = useTranslation();
  if (num >= 1000000000 || num >= 100000000) return t('fmt_number', { val: (num / 1000000000).toFixed(1) }) + 'B';
  if (num >= 1000000 || num >= 100000) return  t('fmt_number', { val: (num / 1000000).toFixed(1) }) + 'M';
  if (num >= 1000) return t('fmt_number', { val: (num / 1000).toFixed(1) }) + 'K';
  return num
}

export const asBool = (val: any) => !!val && val !== 'false';

export const mockFormikError = (fieldName: string, submitCount: number): FormikContextType<any> => submitCount ?
  { errors: { [fieldName]: true }, touched: { [fieldName]: true }, submitCount } as any : {};

export const valuesWithFormat = <F, T extends Record<string, [any, Function?]> = any>(valuesMap: T) => {
  type Values = { [key in keyof T]: any };

  const initialValues = Object.entries(valuesMap)
    .reduce((acc, [k, v]) => ({ ...acc, [k]: v[0] }), {}) as Values;

  const formatValues = (values: Values) => Object.entries(values)
    .reduce((acc, [k, v]) => ({ ...acc, [k]: valuesMap[k][1] ? valuesMap[k][1](v) : v }), {}) as F & Values;

  return { initialValues, formatValues };
};

export const mapObjectValues = <F extends (...args: any) => any>(map: F) =>
  <T extends object>(obj: T): { [key in keyof T]: ReturnType<F> } =>
    obj && Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, map(v)])) as any;

export const ariaHeading = (level: number) => ({ role: 'heading', 'aria-level': level });

declare global {
  interface Number {
    toFixedChkPennyStock(lastPrice?:number, fractionDigits?: number): number;
  }
 }
 Number.prototype.toFixedChkPennyStock = function(lastPrice, fractionDigits): number {
  return (lastPrice ?? this) <= 5 ? this.toFixed(4) : this.toFixed(fractionDigits ?? 2);
}

export const changeTimezone = (date, ianatz) => {

  // suppose the date is 12:00 UTC
  var invdate = new Date(date.toLocaleString('en-US', {
    timeZone: ianatz
  }));

  // then invdate will be 07:00 in Toronto
  // and the diff is 5 hours
  var diff = date.getTime() - invdate.getTime();
  // so 12:00 in Toronto is 17:00 UTC
  return new Date(date.getTime() - diff); // needs to substract

}

export const generateRelativeReactPath = (path: RoutePath | TourRoutePath) => `${window.location.search}#${path}`

export const isMobile = {
  isIpad: () => {
    return !!navigator.userAgent.match(/ipad/i) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  },
  isTablet: () => {
    return !!navigator.userAgent.match(/Tablet|Android|IEMobile|Windows Phone|PlayBook/i);
  },
  isIphone: () => {
    return !!navigator.userAgent.match(/iphone/i);
  },
  isSamsungBrowser: () => {
    return !!navigator.userAgent.match(/SamsungBrowser/i);
  },
  isIOSChrome: () => {
    return !!navigator.userAgent.match('CriOS');
  },
  isAny: () => {
    return (isMobile.isIOSChrome() || isMobile.isSamsungBrowser() || isMobile.isIpad() || isMobile.isTablet() || isMobile.isIphone());
  }
}